<script setup>
import { router } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import CodeField from '@js/components/fields/CodeField.vue';
import EditorField from '@js/components/fields/EditorField.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import SwitchField from '@js/components/fields/SwitchField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { useChangeCase } from '@vueuse/integrations/useChangeCase';
import { reactive, ref, watch } from 'vue';
import { string, object, boolean } from 'yup';

const { tenant, emailingAutomationVariable, emailingAutomationVariableTypes } =
    defineProps({
        tenant: {
            type: Object,
            required: true,
        },
        emailingAutomationVariable: {
            type: Object,
            required: false,
        },
        emailingAutomationVariableTypes: {
            type: Array,
            required: true,
        },
    });

const { route } = useRoute();

const form = reactive({
    name: emailingAutomationVariable ? emailingAutomationVariable.name : '',
    type: emailingAutomationVariable
        ? emailingAutomationVariableTypes.find(
              (type) => type.value === emailingAutomationVariable.type.value,
          )
        : emailingAutomationVariableTypes[0],
    rawValue: emailingAutomationVariable
        ? emailingAutomationVariable.rawValue
        : '',
    isActive: emailingAutomationVariable
        ? emailingAutomationVariable.isActive
        : true,
    startDate: emailingAutomationVariable
        ? emailingAutomationVariable.startDate
        : null,
    endDate: emailingAutomationVariable
        ? emailingAutomationVariable.endDate
        : null,
});

const isSubmitting = ref(false);

const validationSchema = object({
    name: string().required('Ce champ est obligatoire.'),
    type: object().required('Ce champ est obligatoire.'),
    rawValue: string().required('Ce champ est obligatoire.'),
    isActive: boolean().required('Ce champ est obligatoire.'),
    startDate: string().nullable(),
    endDate: string().nullable(),
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route(
                'front.projectManagement.modules.postEmailingAutomationVariable',
                {
                    tenant: tenant.id,
                    ...(emailingAutomationVariable
                        ? {
                              emailingAutomationVariable:
                                  emailingAutomationVariable.id,
                          }
                        : {}),
                },
            ),
            {
                name: useChangeCase(form.name, 'snakeCase').value,
                type: form.type.value,
                rawValue: form.rawValue,
                isActive: form.isActive,
                startDate: form.startDate,
                endDate: form.endDate,
            },
        )
        .then(() => {
            toastSaved();

            router.visit(
                route('front.projectManagement.modules.emailingAutomations', {
                    tenant: tenant.id,
                    tab: 'variables',
                }),
            );
        })
        .catch((error) => {
            toastError(error);
        })
        .finally(() => {
            isSubmitting.value = false;
        });
}

watch(
    () => form.name,
    () => {
        form.name = useChangeCase(form.name, 'snakeCase').value;
    },
);
</script>

<template>
    <FormRoot
        v-slot="{ errors }"
        :validation-schema="validationSchema"
        :submit="submit"
    >
        <FormColumn>
            <FormContent>
                <FormGrid :columns="2">
                    <ListboxField
                        v-model="form.type"
                        field-id="type"
                        field-name="type"
                        label="Type"
                        :options="emailingAutomationVariableTypes"
                        by="value"
                        required
                    >
                        <template #buttonContent>
                            {{ form.type.label }}
                        </template>

                        <template #optionContent="{ option }">
                            {{ option.label }}
                        </template>
                    </ListboxField>

                    <InputField
                        v-model="form.name"
                        label="Nom"
                        field-name="name"
                        field-id="name"
                        type="text"
                        required
                    />
                </FormGrid>
            </FormContent>

            <FormContent>
                <FormGrid :columns="1">
                    <SwitchField
                        v-model="form.isActive"
                        label="Est actif"
                        field-name="isActive"
                        field-id="isActive"
                    />
                </FormGrid>

                <FormGrid :columns="2">
                    <InputField
                        v-model="form.startDate"
                        label="Date de début"
                        field-name="startDate"
                        field-id="startDate"
                        type="datetime-local"
                    />

                    <InputField
                        v-model="form.endDate"
                        label="Date de fin"
                        field-name="endDate"
                        field-id="endDate"
                        type="datetime-local"
                    />
                </FormGrid>
            </FormContent>

            <FormContent>
                <FormGrid :columns="1">
                    <CodeField
                        v-if="form.type.value === 'html'"
                        v-model="form.rawValue"
                        field-name="rawValue"
                        field-id="rawValue"
                        label="Valeur"
                        size="small"
                    />

                    <EditorField
                        v-if="form.type.value === 'editor'"
                        v-model="form.rawValue"
                        label="Valeur"
                        field-name="rawValue"
                        field-id="rawValue"
                        required
                        size="small"
                    />

                    <InputField
                        v-if="form.type.value === 'string'"
                        v-model="form.rawValue"
                        label="Valeur"
                        field-name="rawValue"
                        field-id="rawValue"
                        type="text"
                        required
                    />

                    <InputField
                        v-if="form.type.value === 'integer'"
                        v-model="form.rawValue"
                        label="Valeur"
                        field-name="rawValue"
                        field-id="rawValue"
                        type="number"
                        step="1"
                        required
                    />

                    <InputField
                        v-if="form.type.value === 'float'"
                        v-model="form.rawValue"
                        label="Valeur"
                        field-name="rawValue"
                        field-id="rawValue"
                        type="number"
                        step="0.01"
                        required
                    />

                    <SwitchField
                        v-if="form.type.value === 'boolean'"
                        v-model="form.rawValue"
                        field-name="rawValue"
                        field-id="rawValue"
                        label="Valeur"
                    />

                    <InputField
                        v-if="form.type.value === 'date'"
                        v-model="form.rawValue"
                        label="Valeur"
                        field-name="rawValue"
                        field-id="rawValue"
                        type="date"
                        required
                    />
                </FormGrid>
            </FormContent>
        </FormColumn>

        <FormFooter :is-submitting="isSubmitting" :errors="errors">
            <template #actions>
                <DeleteAction
                    v-if="emailingAutomationVariable"
                    :url="
                        route(
                            'front.projectManagement.modules.deleteEmailingAutomationVariable',
                            {
                                tenant: tenant.id,
                                emailingAutomationVariable:
                                    emailingAutomationVariable.id,
                            },
                        )
                    "
                    :callback="
                        () => {
                            router.visit(
                                route(
                                    'front.projectManagement.modules.emailingAutomations',
                                    {
                                        tenant: tenant.id,
                                        tab: 'variables',
                                    },
                                ),
                            );
                        }
                    "
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Supprimer
                        </ActionButton>
                    </template>
                </DeleteAction>
            </template>
        </FormFooter>
    </FormRoot>
</template>
